import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { Game } from '@Game/Game';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { takeAction } from '@Managers/ButtonManager/ButtonActions';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { runIf } from '@Tools/RunIf';
import { isBeforeFirstFreeGamesSpin } from '../SpecialGamesHelper';
import { SpecialGamesRetriggerCounter } from '../SpecialGamesRetriggerCounter';
import { SpecialGamesSpinCounter } from '../SpecialGamesSpinCounter';
import { SpecialGamesTotalWin } from '../SpecialGamesTotalWin';

export class FreeGames extends GameComponent {
    private counter: SpecialGamesSpinCounter;
    private retriggerCounter: SpecialGamesRetriggerCounter;
    private totalWin: SpecialGamesTotalWin;

    constructor(public readonly scene: GameScene) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, { class: FreeGames.name, source: LoggerSource.Game, message: 'FreeGames constructor' });
    }

    public onCreate(): void {
        this.counter = new SpecialGamesSpinCounter(this.scene, ESpecialGamesType.FREE_GAMES);
        this.retriggerCounter = new SpecialGamesRetriggerCounter(this.scene, ESpecialGamesType.FREE_GAMES);
        this.totalWin = new SpecialGamesTotalWin(this.scene, ESpecialGamesType.FREE_GAMES);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_MODE_ENTRY, this.onFeatureStart, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.onSpin, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_RESTORE, this.onFeatureRestore, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_RETRIGGER, this.onRetrigger, this);
        EventsCenter.on(EWinPresentationStates.WIN_PRESENTATION_HIDDEN_STATE, this.onWinPresentationHiddenState, this);
    }

    @runIf((t: FreeGames) => t.gameStore.get(EGameStoreKeys.IsFreeGamesMode))
    private startAutoplay(): void {
        const { totalSpins } = this.gameStore.get(EGameStoreKeys.FreeGames);

        if (totalSpins > 0)
            this.gameStore.set(EGameStoreKeys.FreeGamesAutoPlayState, { isEnabled: true, currentCount: totalSpins, totalCount: totalSpins }, true);
    }

    @runIf((t: FreeGames) => t.gameStore.get(EGameStoreKeys.IsFreeGamesMode))
    private syncAutoplay(): void {
        const { totalSpins, currentSpin } = this.gameStore.get(EGameStoreKeys.FreeGames);
        this.gameStore.set(EGameStoreKeys.FreeGamesAutoPlayState, { isEnabled: true, currentCount: totalSpins - currentSpin, totalCount: totalSpins }, true);
    }

    @runIf((t: FreeGames) => t.gameStore.get(EGameStoreKeys.IsFreeGamesMode))
    private onFeatureStart(): void {
        this.counter.sync(true, true);
        this.startAutoplay();
    }

    @runIf((t: FreeGames) => t.gameStore.get(EGameStoreKeys.IsFreeGamesMode))
    private onFeatureRestore(): void {
        this.counter.sync(true, true);
        EventsCenter.once(EGameEvents.ACTION_RESPONSE_PROCESSED, this.syncAutoplay, this);
    }

    @runIf((t: FreeGames) => t.gameStore.get(EGameStoreKeys.IsFreeGamesMode))
    private onSpin(): void {
        EventsCenter.emit(ALL_EVENTS_LIST.SPECIAL_GAMES_PLAYED_COUNTER_CHANGE);
    }

    @runIf((t: FreeGames) => t.gameStore.get(EGameStoreKeys.IsFreeGamesMode))
    private onRetrigger(): void {
        EventsCenter.emit(ALL_EVENTS_LIST.SPECIAL_GAMES_TOTAL_COUNTER_CHANGE);
        this.syncAutoplay();
    }

    @runIf((t: FreeGames) => {
        const beforeFirstFreeGamesSpin = isBeforeFirstFreeGamesSpin(t.gameStore);
        const isPostWinState = t.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.POST_WIN_STATE;
        return beforeFirstFreeGamesSpin && isPostWinState;
    })
    private onWinPresentationHiddenState(): void {
        takeAction(this.scene.game as Game, false);
    }
}
